import React from "react";
import { rgba } from "polished";
import styled from "styled-components";

import { MerchProductFront } from "@services/merch/types/api/merch-product-front";

import TracPage from "@services/tracpage/types/trac-page";
import { hoverMotion } from "../../misc/framer";
import ProductImage from "../product-img";
import ProductInfo from "../product-info";
import { ImageFilter } from "../image-color-filter";
import { TracPageFeatureItemType } from "@services/tracpage/types/trac-page-feature-item";
import { SectionIdentifier } from "@services/tracpage/types/trac-page-section-id";
import { VisisbilityButtons } from "../edit-controls/visibility-buttons";
import { useEditMode } from "../../contexts/edit";

export const ProductItem = ({
  className,
  product,
  hasAnimation,
  // siteData,
  isBig,
  isHighlight,
  noPrice,
  inHome,
  handleClick,
}: {
  className?: string;
  product: MerchProductFront;
  hasAnimation?: boolean;
  siteData?: TracPage;
  isBig?: boolean;
  isHighlight?: boolean;
  noPrice?: boolean;
  inHome?: boolean;
  handleClick?: () => void;
}) => {
  const { editMode } = useEditMode();
  const haveWhite = product.colors.find(
    (el) => el.name === "White" || el.name === "white"
  );
  const defaultColorImage =
    product.colors[0]?.image || product.colors[0]?.images?.[0].src;

  if (haveWhite && !product.image) {
    product.image = haveWhite.image || product.image;
  }

  if (defaultColorImage && !product.image) {
    product.image = defaultColorImage;
  }

  return (
    <ProductContainer
      className={`${className ? className : ""} ${isBig ? "big" : ""} ${
        isHighlight ? "highlight" : ""
      }`}
      onClick={handleClick}
      tabIndex={0}
    >
      <ProductImage
        className={`image-container ${isBig ? "big" : ""}`}
        image={product?.image}
        alt={product.title}
        isJsx={true}
        hasAnimation={hasAnimation}
        variants={hoverMotion}
        isBig={isBig}
        isHighlight={isHighlight}
        inHome={inHome}
        jsxImage={
          <ImageFilter
            fill={""}
            haveHover={true}
            imageSrc={product.image}
            // maskSrc={`${process.env.NEXT_PUBLIC_APP_MEDIA_BASE_URL}/merch-preview/${product.blueprintId}-mask.png`}
          />
        }
      />
      <ProductInfo
        className="product-info"
        product={product}
        noPrice={noPrice}
        isBig={isBig}
        isHighlight={isHighlight}
      />
      {!isHighlight && editMode && (
        <div className="edit-controls">
          <VisisbilityButtons
            itemId={product.id}
            sectionId={SectionIdentifier.MERCH}
            itemType={TracPageFeatureItemType.MerchProduct}
          />
        </div>
      )}
    </ProductContainer>
  );
};

const ProductContainer = styled.div`
  cursor: pointer;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 20px;
  width: 208px;
  height: fit-content;
  flex-shrink: 0;
  flex-grow: 0;
  border: 1px solid ${({ theme }) => rgba(theme.colors.primary, 0.1)};
  background-color: ${({ theme }) => rgba(theme.colors.primary, 0.01)};

  transition: background-color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`},
    border
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};

  &:focus-visible,
  &:hover {
    border: 1px solid ${({ theme }) => rgba(theme.colors.primary, 0.3)};
    background-color: ${({ theme }) => rgba(theme.colors.primary, 0.05)};
  }

  &:focus,
  &:focus-within {
    outline: none;
  }

  .edit-controls {
    opacity: 1;
    width: 100%;
    display: flex;
    justify-content: space-between;
    flex: 1;
    align-items: end;
    gap: 24px;
  }

  .image-container {
    &.big {
      margin-bottom: 80px;
    }
  }

  &.highlight {
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    height: auto;
    padding: 16px;
    @media (max-width: ${({ theme }) => `${theme.breakpoints.tablet}px`}) {
      border: 1px solid ${({ theme }) => rgba(theme.colors.highlightLight, 0.5)};
      background: ${({ theme }) => rgba(theme.colors.highlightLight, 0.02)};
    }
  }
`;
